import axios from "axios";
import * as React from "react";
import Sidebar from "../Dashboard/Sidebar.js";
import { Box, Stack } from "@mui/system";
import { styled } from "@mui/material/styles";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Paper,
  Typography,
  Divider,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import SaveIcon from "@mui/icons-material/Save";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import { ToastContainer, toast } from "react-toastify";

const carBrandNames = require("./VehicleBrands.js");

// --- MODERN STYLED COMPONENTS ---

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "32px",
  borderRadius: "20px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  height: "100%",
});

const FormSectionTitle = styled(Typography)({
  fontWeight: 800,
  fontSize: "1.25rem",
  color: "#1E293B",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

function AddLead() {
  return (
    <RootContainer>
      <Sidebar />
      <AddLeadComponent />
    </RootContainer>
  );
}

const AddLeadComponent = () => {
    const navigate = useNavigate();
    const [vehicleBrandNames, setvehicleBrandNames] = React.useState([]);
    const [vehicleModelNames, setvehicleModelNames] = React.useState([]);
    const [carModelNames, setcarModelNames] = React.useState([]);
    const [formData, setFormData] = React.useState({
        customerName: "",
        contactNumber: "",
        gender: "",
        email: "",
        salesExecutive: "",
        state: "",
        city: "",
        pinCode: "",
        address: "",
        activityLog: "",
        leadType: "",
        leadSource: "",
        budget: "",
        testDrive: "",
        purchaseType: "",
        tradeIn: "",
        dueFollowup: "",
        carAvailable: "",
    });

    const getAllVehicleModels = async () => {
        const token = localStorage.getItem("accessToken");
        const config = { headers: { auth: token } };
        try {
            const response = await axios.get("/vehicle-models", config);
            setcarModelNames(response.data.data);
        } catch (err) {
            console.error(err);
        }
    };

    React.useEffect(() => {
        getAllVehicleModels();
    }, []);

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleBrandChange = (event) => {
        setvehicleBrandNames(typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value);
    };

    const handleModelChange = (event) => {
        setvehicleModelNames(typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value);
    };

    const handleSubmit = async () => {
        // Logic for submitting lead
        toast.info("Submitting lead data...");
        // This is a placeholder for the actual API call which wasn't fully implemented in the original file
    };

    return (
        <ContentArea>
            {/* HEADER SECTION */}
            <Stack 
                direction={{ xs: "column", md: "row" }} 
                justifyContent="space-between" 
                alignItems={{ xs: "flex-start", md: "center" }} 
                spacing={3} 
                sx={{ mb: 4 }}
            >
                <Box>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 800,
                            color: "#1E293B",
                            fontFamily: "Kamerik",
                            letterSpacing: "-1.5px",
                            fontSize: { xs: "2rem", md: "3rem" }
                        }}
                    >
                        Register Lead
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748B", fontWeight: 500 }}>
                        Onboard a new potential customer into the CRM pipeline
                    </Typography>
                </Box>
                
                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit}
                    sx={{ 
                        borderRadius: "12px", 
                        px: 4, 
                        bgcolor: "#eb1d27", 
                        "&:hover": { bgcolor: "#9e1c29" },
                        height: "48px",
                        fontWeight: 700
                    }}
                >
                    Save Lead Details
                </Button>
            </Stack>

            <Grid container spacing={4}>
                {/* PERSONAL DETAILS SECTION */}
                <Grid item xs={12} lg={7}>
                    <GlassCard>
                        <FormSectionTitle>
                            <ContactPageIcon sx={{ color: "#eb1d27" }} /> Personal Portfolio
                        </FormSectionTitle>
                        <Divider sx={{ mb: 4, borderColor: "#F1F5F9" }} />
                        
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Full Name" name="customerName" onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Contact Reference" name="contactNumber" onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Gender Identification</InputLabel>
                                    <Select name="gender" onChange={handleInputChange} label="Gender Identification" sx={{ borderRadius: "12px" }}>
                                        <MenuItem value="Male">Male</MenuItem>
                                        <MenuItem value="Female">Female</MenuItem>
                                        <MenuItem value="Other">Other</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Electronic Mail" name="email" onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Assigned Executive</InputLabel>
                                    <Select name="salesExecutive" onChange={handleInputChange} label="Assigned Executive" sx={{ borderRadius: "12px" }}>
                                        <MenuItem value="Riyaz Ahmed">Riyaz Ahmed</MenuItem>
                                        <MenuItem value="Ahsan Ali">Ahsan Ali</MenuItem>
                                        <MenuItem value="Saif Ahmed">Saif Ahmed</MenuItem>
                                        <MenuItem value="Uzair Nadeem">Uzair Nadeem</MenuItem>
                                        <MenuItem value="Saad Ahmed">Saad Ahmed</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>State/Region</InputLabel>
                                    <Select name="state" onChange={handleInputChange} label="State/Region" sx={{ borderRadius: "12px" }}>
                                        {["Andhra Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttarakhand", "Uttar Pradesh", "West Bengal"].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="City/Locality" name="city" onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField fullWidth label="Postal Code" name="pinCode" onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Complete Address" name="address" onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Internal Activity Notes" name="activityLog" multiline rows={4} onChange={handleInputChange} sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }} />
                            </Grid>
                        </Grid>
                    </GlassCard>
                </Grid>

                {/* LEAD DETAILS SECTION */}
                <Grid item xs={12} lg={5}>
                    <GlassCard>
                        <FormSectionTitle>
                            <AssignmentIcon sx={{ color: "#eb1d27" }} /> Engagement Specs
                        </FormSectionTitle>
                        <Divider sx={{ mb: 4, borderColor: "#F1F5F9" }} />
                        
                        <Grid container spacing={2.5}>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Lead Intensity</InputLabel>
                                    <Select name="leadType" onChange={handleInputChange} label="Lead Intensity" sx={{ borderRadius: "12px" }}>
                                        <MenuItem value="Hot">Hot</MenuItem>
                                        <MenuItem value="Warm">Warm</MenuItem>
                                        <MenuItem value="Cold">Cold</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Inbound Source</InputLabel>
                                    <Select name="leadSource" onChange={handleInputChange} label="Inbound Source" sx={{ borderRadius: "12px" }}>
                                        {["OLX", "Car Trade", "Direct Call", "Social Media", "Referrals", "Facebook", "Whatsapp", "Instagram", "Website", "Events", "Showroom Visit"].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Preferred Brands</InputLabel>
                                    <Select 
                                        multiple 
                                        value={vehicleBrandNames} 
                                        onChange={handleBrandChange} 
                                        input={<OutlinedInput label="Preferred Brands" sx={{ borderRadius: "12px" }} />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                                            </Box>
                                        )}
                                    >
                                        {carBrandNames.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Target Model(s)</InputLabel>
                                    <Select 
                                        multiple 
                                        value={vehicleModelNames} 
                                        onChange={handleModelChange} 
                                        input={<OutlinedInput label="Target Model(s)" sx={{ borderRadius: "12px" }} />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((value) => <Chip key={value} label={value} size="small" />)}
                                            </Box>
                                        )}
                                    >
                                        {carModelNames.map((m) => <MenuItem key={m.ModelName} value={m.ModelName}>{m.ModelName}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Budget Range</InputLabel>
                                    <Select name="budget" onChange={handleInputChange} label="Budget Range" sx={{ borderRadius: "12px" }}>
                                        {["Under 5 Lakhs", "Under 10 Lakhs", "Under 20 Lakhs", "Under 50 Lakhs", "Under 1 Crore", "Above 1 Crore"].map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Trial Request</InputLabel>
                                    <Select name="testDrive" onChange={handleInputChange} label="Trial Request" sx={{ borderRadius: "12px" }}>
                                        <MenuItem value="Yes">Yes</MenuItem>
                                        <MenuItem value="No">No</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Acquisition Mode</InputLabel>
                                    <Select name="purchaseType" onChange={handleInputChange} label="Acquisition Mode" sx={{ borderRadius: "12px" }}>
                                        <MenuItem value="Self Bought">Outright Purchase</MenuItem>
                                        <MenuItem value="Finance">Financial Services</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Trade-In Swap</InputLabel>
                                    <Select name="tradeIn" onChange={handleInputChange} label="Trade-In Swap" sx={{ borderRadius: "12px" }}>
                                        <MenuItem value="Yes">Yes</MenuItem>
                                        <MenuItem value="No">No</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </GlassCard>
                </Grid>
            </Grid>
            
            <ToastContainer position="bottom-right" />
        </ContentArea>
    );
};

export default AddLead;
